import { CheckoutConfigOptionGroupStep } from "~generated/graphqlTypes";
import { ConfiguratorContext_CheckoutConfigFragment } from "./Configurator.context.generated";

// Helper function to sort options on Exterior/Interior/Option/AddOnOrStructural steps
export function sortOptionGroups(
  optionGroups: ConfiguratorContext_CheckoutConfigFragment["optionGroups"],
): ConfiguratorContext_CheckoutConfigFragment["optionGroups"] {
  // Separate addOnOrStructural groups from other groups since they have different sorting needs
  // Addon, Structural, and Interior Personalization groups typically contain only one option per group
  const [nonPackageGroups, otherGroups] = optionGroups.partition((ccog) => {
    return (
      ccog.step === CheckoutConfigOptionGroupStep.OptionsAddon ||
      ccog.step === CheckoutConfigOptionGroupStep.OptionsStructural ||
      ccog.step === CheckoutConfigOptionGroupStep.InteriorPersonalization
    );
  });
  // AddOnOrStructural options and Interior Personalization groups
  // Groups may have multiple options, so we need to find the selected option and sort by that
  const sortedNonPackageGroups = nonPackageGroups.sortBy((ccgo) => {
    const selectedOption = ccgo.options.find((option) => option.isSelected);
    const price = selectedOption?.totalPriceInCents ?? ccgo.options.first?.totalPriceInCents ?? 0;

    return [
      ccgo.group.checkoutOrder ?? Number.MAX_SAFE_INTEGER,
      !!selectedOption ? 0 : 1,
      price, // Price ascending
      ccgo.group.name,
    ];
  });

  // Stitch both groups back together
  return [...otherGroups, ...sortedNonPackageGroups].map((ccog) => {
    // Sort options within each group: selected first, then by price, then by name
    return { ...ccog, options: sortOptions(ccog.options) };
  });
}

/**
 * Sorts options by: selected first, then by price ascending, then by name.
 */
export function sortOptions<
  T extends {
    isSelected: boolean;
    totalPriceInCents?: number | null;
    option: { name: string };
  },
>(options: T[]): T[] {
  return options.sortBy((option) => [
    option.isSelected ? 0 : 1,
    option.totalPriceInCents ?? 0, // Price ascending
    option.option.name,
  ]);
}
